import { addSessionEvent, addSessionEventsBatch } from '@/db/session-events';
import type {
  NewSessionEvent,
  PlayerOrTeamId,
  RoundId,
  Session,
  SessionId,
  UserId,
} from '@/schemas';
import { createRoundId } from '@/schemas';
import { createOperationId } from '../../operation-queue';
import { createSystemEvent } from '../../session-helpers';
import type { QueueDependencies } from '../queue-utils';

type EndSessionQueueParams = QueueDependencies & {
  sessionId: SessionId;
  events: NewSessionEvent[];
  timestamp: number;
  winner: PlayerOrTeamId | undefined;
  previousStatus: Session['status'];
  previousEndedAt: number | undefined;
  previousWinner: PlayerOrTeamId | undefined;
  previousRoundState?: {
    roundId: RoundId;
    completedAt: number | undefined;
    updatedAt: number;
  };
};

type PauseSessionQueueParams = QueueDependencies & {
  sessionId: SessionId;
  previousStatus: Session['status'];
  previousPausedAt: number | undefined;
  userId: UserId | undefined;
  timestamp: number;
};

type ResumeSessionQueueParams = QueueDependencies & {
  sessionId: SessionId;
  previousStatus: Session['status'];
  previousPausedAt: number | undefined;
  userId: UserId | undefined;
};

type RestartSessionQueueParams = QueueDependencies & {
  sessionId: SessionId;
  previousStatus: Session['status'];
  previousPausedAt: number | undefined;
  previousEndedAt: number | undefined;
  previousCurrentRoundNumber: number;
  userId: UserId | undefined;
};

export const enqueueEndSession = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  events,
  timestamp,
  winner,
  previousStatus,
  previousEndedAt,
  previousWinner,
  previousRoundState,
}: EndSessionQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'endSession',
    dedupKey: `session:end:${sessionId}`,
    priority: 4,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = 'finished';
        draft.activeSession.endedAt = timestamp;
        draft.activeSession.winner = winner;
        if (previousRoundState) {
          const round = draft.activeSession.rounds.find(
            (candidate) => candidate.id === previousRoundState.roundId,
          );
          if (round) {
            round.completedAt = timestamp;
            round.updatedAt = timestamp;
          }
        }
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = previousStatus;
        draft.activeSession.endedAt = previousEndedAt;
        draft.activeSession.winner = previousWinner;
        if (previousRoundState) {
          const round = draft.activeSession.rounds.find(
            (candidate) => candidate.id === previousRoundState.roundId,
          );
          if (round) {
            round.completedAt = previousRoundState.completedAt;
            round.updatedAt = previousRoundState.updatedAt;
          }
        }
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEventsBatch(events);
      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};

export const enqueuePauseSession = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  sessionId,
  previousStatus,
  previousPausedAt,
  userId,
  timestamp,
}: PauseSessionQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'pauseSession',
    dedupKey: `session:pause:${sessionId}`,
    priority: 7,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = 'paused';
        draft.activeSession.pausedAt = timestamp;
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = previousStatus;
        draft.activeSession.pausedAt = previousPausedAt;
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(createSystemEvent(sessionId, 'SessionPaused', {}, userId));
    },
  });
};

export const enqueueResumeSession = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  sessionId,
  previousStatus,
  previousPausedAt,
  userId,
}: ResumeSessionQueueParams): Promise<void> => {
  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'resumeSession',
    dedupKey: `session:resume:${sessionId}`,
    priority: 7,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = 'ongoing';
        draft.activeSession.pausedAt = undefined;
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = previousStatus;
        draft.activeSession.pausedAt = previousPausedAt;
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      await addSessionEvent(createSystemEvent(sessionId, 'SessionResumed', {}, userId));
    },
  });
};

export const enqueueRestartSession = async ({
  operationQueue,
  set,
  refreshSessionFromEvents,
  updateUndoRedoAvailability,
  sessionId,
  previousStatus,
  previousPausedAt,
  previousEndedAt,
  previousCurrentRoundNumber,
  userId,
}: RestartSessionQueueParams): Promise<void> => {
  const roundId = createRoundId();
  const timestamp = Date.now();

  await operationQueue.enqueue<void>({
    id: createOperationId(),
    type: 'restartSession',
    dedupKey: `session:restart:${sessionId}`,
    priority: 4,
    optimisticUpdate: () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = 'ongoing';
        draft.activeSession.pausedAt = undefined;
        draft.activeSession.endedAt = undefined;
        draft.activeSession.currentRoundNumber = 1;
        // Clear rounds (they'll be filtered out by event replay anyway)
        draft.activeSession.rounds = [];
        // Add the first round optimistically
        draft.activeSession.rounds.push({
          id: roundId,
          sessionId,
          number: 1,
          name: undefined,
          scores: {},
          notes: undefined,
          tags: undefined,
          imageIds: [],
          createdAt: timestamp,
          updatedAt: timestamp,
          deletedAt: undefined,
        });
      });
    },
    rollback: async () => {
      set((draft) => {
        if (!draft.activeSession || draft.activeSession.id !== sessionId) {
          return;
        }
        draft.activeSession.status = previousStatus;
        draft.activeSession.pausedAt = previousPausedAt;
        draft.activeSession.endedAt = previousEndedAt;
        draft.activeSession.currentRoundNumber = previousCurrentRoundNumber;
      });
      await refreshSessionFromEvents(sessionId);
    },
    execute: async () => {
      const events: NewSessionEvent[] = [
        createSystemEvent(sessionId, 'SessionRestarted', {}, userId),
        createSystemEvent(
          sessionId,
          'RoundStarted',
          {
            roundId,
            number: 1,
            name: undefined,
          },
          userId,
        ),
      ];
      await addSessionEventsBatch(events);
      await updateUndoRedoAvailability(sessionId);
      await refreshSessionFromEvents(sessionId);
    },
  });
};
